import { useState } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, IconButton, CircularProgress,
} from '@mui/material';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

/**
 * Shared building blocks for the branded portal sign-in screens (clinic staff, patient, pharmacy).
 *
 * All three screens share one visual system — the reception-photo background, the left value-prop
 * panel, and the floating white card — and differ only in copy, feature list, and which secondary
 * actions appear. This module owns the system so the three screens stay in lock-step; each screen
 * supplies its content and its own form/auth wiring.
 */

// ── Design tokens ──
export const NAVY = '#0b1c30';
export const HEADING_NAVY = '#0f2942';
export const ACCENT_BLUE = '#2f6bff';
export const ACCENT_CYAN = '#12b5cf';
export const TEAL = '#2fe0c8';
export const SLATE = '#64748b';
export const FIELD_BORDER = '#e2e8f0';

/** Blue→cyan gradient shared by the logo mark and the primary button. */
export const BRAND_GRADIENT = `linear-gradient(90deg, ${ACCENT_BLUE} 0%, ${ACCENT_CYAN} 100%)`;

const BG_IMAGE = '/assets/images/login-background.png';

/** Shared outlined-field styling: rounded, white, gray placeholder, brand focus ring. */
export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    fontSize: '0.95rem',
    '& fieldset': { borderColor: FIELD_BORDER },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: ACCENT_BLUE, borderWidth: '1.5px' },
  },
  '& .MuiOutlinedInput-input': { paddingTop: '14px', paddingBottom: '14px' },
  '& .MuiOutlinedInput-input::placeholder': { color: '#94a3b8', opacity: 1 },
};

/**
 * Compact alert styling. A default MUI Alert is tall enough that showing one tips the card past the
 * viewport and makes the whole page scroll — trimming the padding/font keeps a single-line error
 * inside the fold on a normal screen.
 */
export const alertSx = {
  mb: 1.5,
  borderRadius: '12px',
  py: 0.25,
  fontSize: '0.83rem',
  alignItems: 'center',
  '& .MuiAlert-message': { py: 0.5, fontSize: '0.83rem' },
  '& .MuiAlert-icon': { py: 0.75, fontSize: '1.15rem' },
};

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', mb: 0.5 }}>
      {children}
    </Typography>
  );
}

/** ClinVea wordmark (icon + "ClinVea"), the dark-variant logo asset. */
export function ClinveaWordmark({ height = 100 }: { height?: number }) {
  return (
    <Box
      component="img"
      src="/assets/images/clinvea-logo-dark.png?v=20261007"
      alt="ClinVea"
      sx={{ height, width: 'auto', display: 'block', userSelect: 'none' }}
      draggable={false}
    />
  );
}

export interface Feature {
  icon: LucideIcon;
  title: string;
  desc: string;
}

/** "or" divider used between the primary sign-in and any secondary action. */
export function OrDivider() {
  const { t } = useTranslation('auth');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.75 }}>
      <Box sx={{ flex: 1, height: '1px', backgroundColor: FIELD_BORDER }} />
      <Typography sx={{ color: SLATE, fontSize: '0.85rem' }}>{t('shared.or')}</Typography>
      <Box sx={{ flex: 1, height: '1px', backgroundColor: FIELD_BORDER }} />
    </Box>
  );
}

/**
 * The gradient primary submit button. `children` is the idle label; while `loading` a spinner is
 * prepended and `loadingLabel` (falling back to the idle label) is shown instead.
 *
 * The label always lives inside the same <span> so React only ever updates a text node's value and
 * inserts/removes the spinner *element*. Rendering the label as a bare text node that gets swapped
 * out on submit crashed the screen ("Failed to execute 'removeChild' on 'Node'") whenever a
 * browser translator or extension had wrapped that text node in its own <font>/<span> — React then
 * tried to remove a node that was no longer a direct child of the button.
 */
export function AuthSubmitButton({
  children, disabled, loading = false, loadingLabel,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: React.ReactNode;
}) {
  return (
    <Button
      fullWidth
      type="submit"
      variant="contained"
      disableElevation
      disabled={disabled || loading}
      sx={{
        py: 1.25, fontSize: '1rem', fontWeight: 700, borderRadius: '12px', textTransform: 'none',
        background: BRAND_GRADIENT,
        boxShadow: `0 12px 24px ${ACCENT_BLUE}33`,
        '&:hover': { background: BRAND_GRADIENT, filter: 'brightness(1.05)' },
        '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
      }}
    >
      {loading && <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />}
      <span>{loading ? (loadingLabel ?? children) : children}</span>
    </Button>
  );
}

/**
 * Email + password fields with the leading mail/lock icons and the password eye toggle. Owns its
 * own show/hide state; the screen supplies value/handlers and per-field errors.
 */
export function AuthEmailPassword({
  email, password, onEmail, onPassword, disabled = false,
  emailError, passwordError,
}: {
  email: string;
  password: string;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  disabled?: boolean;
  emailError?: string;
  passwordError?: string;
}) {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  return (
    <>
      <FieldLabel>{t('field.emailAddress')}</FieldLabel>
      <TextField
        fullWidth
        type="email"
        placeholder={t('placeholder.email')}
        value={email}
        onChange={(e) => onEmail(e.target.value)}
        disabled={disabled}
        autoComplete="email"
        error={!!emailError}
        helperText={emailError}
        sx={{ ...fieldSx, mb: 1.75 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Mail size={18} color="#94a3b8" /></InputAdornment>
          ),
        }}
      />

      <FieldLabel>{t('field.password')}</FieldLabel>
      <TextField
        fullWidth
        type={showPassword ? 'text' : 'password'}
        placeholder={t('placeholder.password')}
        value={password}
        onChange={(e) => onPassword(e.target.value)}
        disabled={disabled}
        autoComplete="current-password"
        error={!!passwordError}
        helperText={passwordError}
        sx={{ ...fieldSx, mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Lock size={18} color="#94a3b8" /></InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? t('action.hidePassword') : t('action.showPassword')}
                onClick={() => setShowPassword((s) => !s)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                disabled={disabled}
                sx={{ color: '#94a3b8' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
}

/**
 * The full portal shell: background + overlay, the left value-prop panel, and the white card with
 * its lock badge / title / subtitle. The screen's form and secondary actions are the {children}.
 */
export function AuthScreenLayout({
  heading, subtext, features, cardTitle, cardSubtitle, children,
}: {
  heading: { pre: string; accent: string };
  subtext: string;
  features: Feature[];
  cardTitle: string;
  cardSubtitle: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('auth');
  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: NAVY,
        // Dark enough on the far left to keep the headline legible, then fades off through the
        // middle so the reception desk and the glowing plus in the photo stay visible.
        backgroundImage:
          `linear-gradient(105deg, rgba(9,20,36,0.90) 0%, rgba(9,20,36,0.70) 26%, rgba(9,20,36,0.34) 52%, rgba(9,20,36,0.26) 100%), url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 2, sm: 3, md: 5, lg: 7 },
        py: { xs: 2.5, md: 3 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '1.05fr minmax(410px, 0.85fr)' },
          alignItems: 'center',
          columnGap: { lg: 4.5, xl: 5 },
          rowGap: 4,
        }}
      >
        {/* ── Left: brand + value props (over the reception image) ── */}
        <Box sx={{ display: { xs: 'none', lg: 'block' }, color: '#fff', maxWidth: 560 }}>
          <Box sx={{ mb: { lg: 3.5, xl: 4.5 } }}><ClinveaWordmark height={100} /></Box>

          <Typography sx={{ fontWeight: 800, fontSize: { lg: '2.7rem', xl: '3.3rem' }, lineHeight: 1.08, letterSpacing: '-0.02em' }}>
            {heading.pre}
            <br />
            <Box component="span" sx={{ color: TEAL }}>{heading.accent}</Box>
          </Typography>

          <Typography sx={{ mt: 2, mb: { lg: 3, xl: 4 }, color: 'rgba(226,232,240,0.82)', fontSize: { lg: '0.98rem', xl: '1.05rem' }, lineHeight: 1.6, maxWidth: 440 }}>
            {subtext}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { lg: 1.75, xl: 2.25 } }}>
            {features.map(({ icon: Icon, title, desc }) => (
              <Box key={title} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 46, height: 46, flexShrink: 0, borderRadius: '12px', display: 'grid',
                    placeItems: 'center', color: TEAL,
                    backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <Icon size={21} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.02rem', color: '#fff', lineHeight: 1.2 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(203,213,225,0.75)', fontSize: '0.9rem' }}>
                    {desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Right: sign-in card ── */}
        <Box sx={{ justifySelf: { xs: 'center', lg: 'end' }, width: '100%', maxWidth: 480 }}>
          {/* Compact brand for small screens, where the left panel is hidden */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'center', mb: 2 }}>
            <ClinveaWordmark height={72} />
          </Box>

          <Box
            sx={{
              backgroundColor: '#fff', borderRadius: '24px', boxShadow: '0 30px 80px rgba(2,10,25,0.45)',
              p: { xs: 2.5, sm: 3.5 },
            }}
          >
            {/*
              The language choice sits at the top of the sign-in card on every auth screen,
              shown as visible buttons rather than a dropdown. Someone who reads only Telugu
              cannot be asked to discover a setting behind an English tooltip, and this is the
              first screen they see — so it has to be the most discoverable switcher in the app.
            */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, mt: -0.5 }}>
              <LanguageSwitcher variant="inline" />
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
              <Box
                sx={{
                  width: 60, height: 60, mx: 'auto', mb: 1.5, borderRadius: '50%', display: 'grid',
                  placeItems: 'center', color: ACCENT_BLUE,
                  border: `2px solid ${ACCENT_BLUE}22`, boxShadow: `0 0 0 7px ${ACCENT_BLUE}0d`,
                }}
              >
                <Lock size={24} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.55rem', color: HEADING_NAVY }}>
                {cardTitle}
              </Typography>
              <Typography sx={{ color: SLATE, mt: 0.5, fontSize: '0.92rem' }}>
                {cardSubtitle}
              </Typography>
            </Box>

            {children}
          </Box>

          {/* Security reassurance under the card (identical across all portals) */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 1.25, color: 'rgba(203,213,225,0.7)' }}>
            <ShieldCheck size={17} style={{ marginTop: 2, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                {t('shared.encryptionNotice')}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                {t('shared.neverShare')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
